import { apiFetch } from "./apiFetch";
import { trackWarning } from "../telemetry/appInsights";
import { useFormStore } from "../store/formStore";
import { useLockStore } from "../store/lockStore";
import { normalizeStateValue } from "../utils/stateLabels";
import {
  EMPTY_FORM_DATA,
  uuid,
  type IntakeFormResponse,
  type LockResponse,
  type FileUploadResponse,
  type IntakeFormData,
  type Mismatch,
  type SicCodeEntry,
  type Employee,
  type OwnershipAssignment,
} from "../types";
import { PRIMARY_ENTITY_ID, isAffiliatedListShown } from "../types/structureConstants";

/**
 * Raw response shape from the CRM Function backend.
 * Fields are flat and use camelCase (ASP.NET Core / STJ default).
 * The mapToIntakeFormResponse() adapter converts this to the shape
 * our store expects.
 */
interface BackendIntakeResponse {
  id: string;
  name: string;
  status: string;
  opportunityId: string;
  version: number;
  createdAt: string;
  updatedAt: string;
  // Form fields — per-step stories add fields here.
  companyLegalName: string;
  effectiveDate: string;
  sicCode: string;
  sicDescription: string | null;
  dba: string | null;
  federalTaxId: string | null;
  streetAddress: string | null;
  city: string | null;
  state: string | null;
  zipCode: string | null;
  organizationType: string | null;
  yearEstablished: number | null;
  avgW2Employees: number | null;
  avgW2Employees20Weeks: number | null;
  // Step 2: Contact
  contactName: string;
  contactTitle: string;
  contactEmail: string;
  contactPhone: string;
  fiduciarySameAsContact: string | null;
  fiduciaryName: string;
  fiduciaryTitle: string;
  fiduciaryEmail: string;
  fiduciaryPhone: string;
  needAlternatePortalAccess: string | null;
  portalContactName: string;
  portalContactEmail: string;
  portalContactPhone: string;
  portalReplaces: string;
  // Step 3: Company Structure
  hasCba: string | null;
  cbaUnitName: string;
  cbaEffectiveDate: string;
  hasPeo: string | null;
  peoType: string;
  isMultipleEmployer: string | null;
  multipleEmployerType: string;
  isCommonlyControlledGroup: string | null;
  ownershipData: string | null;
  affiliationData: string | null;
  // Step 4: Coverage & Benefits
  replacingMedical: string | null;
  priorMedicalCarrier: string | null;
  priorMedicalCarrierOther: string | null;
  priorMedicalPlanType: string | null;
  priorMedicalEffectiveDate: string | null;
  replacingDental: string | null;
  deductibleYearType: string | null;
  medicalContribution: number | null;
  offeringDental: string | null;
  offeringVision: string | null;
  reimburseCopays: string | null;
  offerHsa: string | null;
  offerHra: string | null;
  otherReimbursement: string | null;
  // Step 5: Eligibility
  excludeClass: string | null;
  excludedClassesData: string | null;
  excludedClassesOther: string | null;
  allowDomesticPartners: string | null;
  minHoursPerWeek: number | null;
  waitingPeriod: string | null;
  waiveWaitingPeriod: string | null;
  employeesInWaitingPeriod: string | null;
  offerCOBRAVoluntary: string | null;
  employeesOnCOBRA: string | null;
  useOptimylCOBRA: string | null;
  wantOptimylSection125: string | null;
  sec125PlanMedical: boolean | null;
  sec125PlanDental: boolean | null;
  sec125PlanVision: boolean | null;
  sec125TierEmployeeOnly: boolean | null;
  sec125TierEmployeeSpouse: boolean | null;
  sec125TierEmployeeChildren: boolean | null;
  sec125TierEmployeeFamily: boolean | null;
  // Step 6: Payment
  paymentMethod: string | null;
  achAccountHolderName: string | null;
  achBankName: string | null;
  achRoutingNumber: string | null;
  achAccountNumber: string | null;
  achAccountType: string | null;
  // Step 7: Documents & Employees
  totalEnrolledEmployees: number | null;
  docVerificationType: string | null;
  censusMembers: Array<{
    id: string;
    firstName: string;
    lastName: string;
    owner: string;
    medicalCoverage: string;
    medicalPlan: string | null;
    dentalCoverage: string | null;
    visionCoverage: string | null;
    cobra: string;
  }> | null;
  newlyFormedCompany: string | null;
  uwMethod: string | null;
  isRenewal: boolean | null;
  availablePlans: number | null;
  ihqLink: string | null;
  // Baseline data for comparison (V1: own fields, V2+: linked record)
  baseline: {
    companyLegalName: string;
    effectiveDate: string;
    sicCode: string;
    dba: string | null;
    federalTaxId: string | null;
    streetAddress: string | null;
    city: string | null;
    state: string | null;
    zipCode: string | null;
    organizationType: string | null;
    yearEstablished: number | null;
    avgW2Employees: number | null;
    avgW2Employees20Weeks: number | null;
    contactName: string;
    contactTitle: string;
    contactEmail: string;
    contactPhone: string;
    fiduciaryName: string;
    fiduciaryTitle: string;
    fiduciaryEmail: string;
    fiduciaryPhone: string;
    portalContactName: string;
    portalContactEmail: string;
    portalContactPhone: string;
    portalReplaces: string;
    hasCba: string | null;
    cbaUnitName: string;
    cbaEffectiveDate: string;
    hasPeo: string | null;
    peoType: string;
    isMultipleEmployer: string | null;
    multipleEmployerType: string;
    ownershipData: string | null;
    affiliationData: string | null;
    replacingMedical: string | null;
    priorMedicalCarrier: string | null;
    priorMedicalCarrierOther: string | null;
    priorMedicalPlanType: string | null;
    priorMedicalEffectiveDate: string | null;
    replacingDental: string | null;
    deductibleYearType: string | null;
    medicalContribution: number | null;
    offeringDental: string | null;
    offeringVision: string | null;
    reimburseCopays: string | null;
    offerHsa: string | null;
    offerHra: string | null;
    otherReimbursement: string | null;
    paymentMethod: string | null;
    achAccountHolderName: string | null;
    achBankName: string | null;
    achRoutingNumber: string | null;
    achAccountNumber: string | null;
    achAccountType: string | null;
  } | null;
  files: Array<{ fileId: string; name: string; size: number; type: string; docType: string }> | null;
  meta: {
    organizationTypeOptions: string[];
    medicalCarrierOptions: string[];
    excludedClassesOptions: string[];
    waitingPeriodOptions: string[];
  } | null;
}

function tryParseJsonArray(json: string | null, fieldName: string): string[] {
  if (!json) return [];
  try {
    const parsed = JSON.parse(json);
    if (Array.isArray(parsed)) return parsed.map(String);
    return [];
  } catch (err) {
    trackWarning(`Failed to parse ${fieldName}`, { error: String(err) });
    return [];
  }
}

/**
 * Maps the flat backend response to the nested shape our formStore
 * expects. Add field mappings here as per-step stories add fields
 * to the backend.
 */
// Allowed union values — kept inline as readonly arrays for narrow typing via `as const`.
const VALID_YES_NO = ["Yes", "No", ""] as const;
const VALID_COVERAGE = ["Enrolling", "Waiving", "Waiting Period", "N/A", ""] as const;
const VALID_MEDICAL_PLAN = ["Plan 1", "Plan 2", "Plan 3", "Plan 4", ""] as const;

function narrow<T extends readonly string[]>(
  raw: string | null | undefined,
  allowed: T,
  fieldName: string,
  memberId: string,
): T[number] {
  const v = raw ?? "";
  if ((allowed as readonly string[]).includes(v)) return v as T[number];
  // Unknown picklist value — surface for triage but don't crash the form load.
  trackWarning("Unknown census member field value", { fieldName, value: v, memberId });
  return "" as T[number];
}

function mapCensusMembersToEmployees(members: BackendIntakeResponse["censusMembers"]): Employee[] {
  if (!members || members.length === 0) return [];
  return members.map((m): Employee => ({
    salesforceId: m.id,
    firstName: m.firstName ?? "",
    lastName: m.lastName ?? "",
    owner: narrow(m.owner, VALID_YES_NO, "owner", m.id),
    medicalCoverage: narrow(m.medicalCoverage, VALID_COVERAGE, "medicalCoverage", m.id),
    medicalPlan: narrow(m.medicalPlan, VALID_MEDICAL_PLAN, "medicalPlan", m.id),
    dentalCoverage: narrow(m.dentalCoverage, VALID_COVERAGE, "dentalCoverage", m.id),
    visionCoverage: narrow(m.visionCoverage, VALID_COVERAGE, "visionCoverage", m.id),
    cobra: narrow(m.cobra, VALID_YES_NO, "cobra", m.id),
    source: "manual",
  }));
}

function mapToIntakeFormResponse(raw: BackendIntakeResponse): IntakeFormResponse {
  // Build baseline dict + prefilled map + mismatches from the V1 snapshot.
  const baseline: Record<string, unknown> = {};
  const prefilled: Record<string, boolean> = {};
  const mismatches: Record<string, Mismatch> = {};

  if (raw.baseline) {
    // Normalize nullable numbers to strings for the generic string-compare path.
    // Empty string ("") is treated as "baseline not set" — no prefill hint shown.
    const toStr = (v: string | number | null | undefined) =>
      v === null || v === undefined ? "" : String(v);
    const checks: Array<{ key: string; current: string; baselineValue: string }> = [
      { key: "companyLegalName", current: toStr(raw.companyLegalName), baselineValue: toStr(raw.baseline.companyLegalName) },
      { key: "effectiveDate", current: toStr(raw.effectiveDate), baselineValue: toStr(raw.baseline.effectiveDate) },
      { key: "sicCode", current: toStr(raw.sicCode), baselineValue: toStr(raw.baseline.sicCode) },
      { key: "dba", current: toStr(raw.dba), baselineValue: toStr(raw.baseline.dba) },
      { key: "federalTaxId", current: toStr(raw.federalTaxId), baselineValue: toStr(raw.baseline.federalTaxId) },
      { key: "streetAddress", current: toStr(raw.streetAddress), baselineValue: toStr(raw.baseline.streetAddress) },
      { key: "city", current: toStr(raw.city), baselineValue: toStr(raw.baseline.city) },
      { key: "state", current: toStr(raw.state), baselineValue: toStr(raw.baseline.state) },
      { key: "zipCode", current: toStr(raw.zipCode), baselineValue: toStr(raw.baseline.zipCode) },
      { key: "orgType", current: toStr(raw.organizationType), baselineValue: toStr(raw.baseline.organizationType) },
      { key: "yearEstablished", current: toStr(raw.yearEstablished), baselineValue: toStr(raw.baseline.yearEstablished) },
      { key: "avgW2Employees", current: toStr(raw.avgW2Employees), baselineValue: toStr(raw.baseline.avgW2Employees) },
      { key: "avgW2Employees20Weeks", current: toStr(raw.avgW2Employees20Weeks), baselineValue: toStr(raw.baseline.avgW2Employees20Weeks) },
      { key: "contactName", current: toStr(raw.contactName), baselineValue: toStr(raw.baseline.contactName) },
      { key: "contactTitle", current: toStr(raw.contactTitle), baselineValue: toStr(raw.baseline.contactTitle) },
      { key: "contactEmail", current: toStr(raw.contactEmail), baselineValue: toStr(raw.baseline.contactEmail) },
      { key: "contactPhone", current: toStr(raw.contactPhone), baselineValue: toStr(raw.baseline.contactPhone) },
      { key: "fiduciaryName", current: toStr(raw.fiduciaryName), baselineValue: toStr(raw.baseline.fiduciaryName) },
      { key: "fiduciaryTitle", current: toStr(raw.fiduciaryTitle), baselineValue: toStr(raw.baseline.fiduciaryTitle) },
      { key: "fiduciaryEmail", current: toStr(raw.fiduciaryEmail), baselineValue: toStr(raw.baseline.fiduciaryEmail) },
      { key: "fiduciaryPhone", current: toStr(raw.fiduciaryPhone), baselineValue: toStr(raw.baseline.fiduciaryPhone) },
      { key: "portalContactName", current: toStr(raw.portalContactName), baselineValue: toStr(raw.baseline.portalContactName) },
      { key: "portalContactEmail", current: toStr(raw.portalContactEmail), baselineValue: toStr(raw.baseline.portalContactEmail) },
      { key: "portalContactPhone", current: toStr(raw.portalContactPhone), baselineValue: toStr(raw.baseline.portalContactPhone) },
      { key: "portalReplaces", current: toStr(raw.portalReplaces), baselineValue: toStr(raw.baseline.portalReplaces) },
      { key: "replacingMedical", current: toStr(raw.replacingMedical), baselineValue: toStr(raw.baseline.replacingMedical) },
      { key: "priorMedicalCarrier", current: toStr(raw.priorMedicalCarrier), baselineValue: toStr(raw.baseline.priorMedicalCarrier) },
      { key: "priorMedicalCarrierOther", current: toStr(raw.priorMedicalCarrierOther), baselineValue: toStr(raw.baseline.priorMedicalCarrierOther) },
      { key: "priorMedicalPlanType", current: toStr(raw.priorMedicalPlanType), baselineValue: toStr(raw.baseline.priorMedicalPlanType) },
      { key: "priorMedicalEffectiveDate", current: toStr(raw.priorMedicalEffectiveDate), baselineValue: toStr(raw.baseline.priorMedicalEffectiveDate) },
      { key: "replacingDental", current: toStr(raw.replacingDental), baselineValue: toStr(raw.baseline.replacingDental) },
      { key: "deductibleYearType", current: toStr(raw.deductibleYearType), baselineValue: toStr(raw.baseline.deductibleYearType) },
      { key: "medicalContribution", current: toStr(raw.medicalContribution), baselineValue: toStr(raw.baseline.medicalContribution) },
      { key: "offeringDental", current: toStr(raw.offeringDental), baselineValue: toStr(raw.baseline.offeringDental) },
      { key: "offeringVision", current: toStr(raw.offeringVision), baselineValue: toStr(raw.baseline.offeringVision) },
      { key: "reimburseCopays", current: toStr(raw.reimburseCopays), baselineValue: toStr(raw.baseline.reimburseCopays) },
      { key: "offerHsa", current: toStr(raw.offerHsa), baselineValue: toStr(raw.baseline.offerHsa) },
      { key: "offerHra", current: toStr(raw.offerHra), baselineValue: toStr(raw.baseline.offerHra) },
      { key: "otherReimbursement", current: toStr(raw.otherReimbursement), baselineValue: toStr(raw.baseline.otherReimbursement) },
      { key: "paymentMethod", current: toStr(raw.paymentMethod), baselineValue: toStr(raw.baseline.paymentMethod) },
      { key: "achAccountHolderName", current: toStr(raw.achAccountHolderName), baselineValue: toStr(raw.baseline.achAccountHolderName) },
      { key: "achBankName", current: toStr(raw.achBankName), baselineValue: toStr(raw.baseline.achBankName) },
      { key: "achRoutingNumber", current: toStr(raw.achRoutingNumber), baselineValue: toStr(raw.baseline.achRoutingNumber) },
      { key: "achAccountNumber", current: toStr(raw.achAccountNumber), baselineValue: toStr(raw.baseline.achAccountNumber) },
      { key: "achAccountType", current: toStr(raw.achAccountType), baselineValue: toStr(raw.baseline.achAccountType) },
    ];
    for (const { key, current, baselineValue } of checks) {
      if (baselineValue) {
        baseline[key] = baselineValue;
        prefilled[key] = true;
        // Match useFormField's runtime check: any difference (including empty)
        // is a mismatch — otherwise an SF-side blank-out wouldn't surface.
        // The `state` field is stored full-name on the form but 2-letter on the
        // Account, so normalize both sides before comparing to avoid a false
        // "differs from proposal" banner on every load.
        const [curForCompare, baseForCompare] =
          key === "state"
            ? [normalizeStateValue(current), normalizeStateValue(baselineValue)]
            : [current, baselineValue];
        if (curForCompare !== baseForCompare) {
          mismatches[key] = { original: baselineValue, current };
        }
      }
    }
  }

  // EMPTY_FORM_DATA first for typed defaults on every field; backend overlays
  // fill in the fields the SF object actually returns. Per-step stories add
  // more overlays here as the SF object grows.
  //
  // Ownership: new shape is `{ owners: [...name-only...], assignments: [...] }`.
  // Legacy shape was a flat array of owners with ownership%/eligible/enrolling/W2
  // on the owner itself — those extra fields are silently dropped (UAT wipe;
  // per ADO-3490 there is no backwards-compatibility migration).
  let owners = EMPTY_FORM_DATA.owners;
  let ownershipAssignments: OwnershipAssignment[] = [];
  let affiliatedGroups = EMPTY_FORM_DATA.affiliatedGroups;
  let primaryEligibleEmployees: number | null = null;
  try {
    if (raw.ownershipData) {
      const parsed = JSON.parse(raw.ownershipData);
      const parseOwnerName = (o: Record<string, unknown>) => ({
        id: String(o.id ?? uuid()),
        firstName: String(o.firstName ?? ""),
        lastName: String(o.lastName ?? ""),
        mi: String(o.mi ?? ""),
      });
      if (Array.isArray(parsed)) {
        // Legacy flat-array shape — take names only, drop ownership assignments entirely.
        owners = parsed.map(parseOwnerName);
        ownershipAssignments = [];
      } else if (parsed && typeof parsed === "object") {
        const ownersArr = Array.isArray((parsed as Record<string, unknown>).owners)
          ? ((parsed as Record<string, unknown>).owners as Record<string, unknown>[])
          : [];
        owners = ownersArr.map(parseOwnerName);
        const assignmentsArr = Array.isArray((parsed as Record<string, unknown>).assignments)
          ? ((parsed as Record<string, unknown>).assignments as Record<string, unknown>[])
          : [];
        ownershipAssignments = assignmentsArr.map((a) => ({
          id: String(a.id ?? uuid()),
          ownerId: String(a.ownerId ?? ""),
          entityId: String(a.entityId ?? ""),
          ownership: Number(a.ownership) || 0,
          receivingW2Wages: a.receivingW2Wages === true ? true : a.receivingW2Wages === false ? false : null,
          enrollingInBenefits: a.enrollingInBenefits === true ? true : a.enrollingInBenefits === false ? false : null,
        }));
      }
    }
  } catch (err) { trackWarning("Failed to parse ownershipData", { error: String(err) }); }
  try {
    if (raw.affiliationData) {
      const parsed = JSON.parse(raw.affiliationData);
      if (Array.isArray(parsed)) {
        // Split out the primary-group entry (id === PRIMARY_ENTITY_ID) — that lives on
        // the form as its own working field (primaryEligibleEmployees) plus its Step 1
        // fields (legal name / EIN) are already carried elsewhere. The remaining entries
        // are the affiliated entities the user sees in the Entity 2+ cards. Any legacy
        // `eligible` field is silently ignored — the per-entity Eligible for Benefits
        // question was removed by ADO-3490.
        const rows = parsed as Record<string, unknown>[];
        const primaryEntry = rows.find((g) => g.id === PRIMARY_ENTITY_ID);
        if (primaryEntry) {
          primaryEligibleEmployees = primaryEntry.employees === null || primaryEntry.employees === undefined
            ? null
            : Number(primaryEntry.employees) || 0;
        }
        affiliatedGroups = rows
          .filter((g) => g.id !== PRIMARY_ENTITY_ID)
          .map((g) => ({
            id: String(g.id ?? uuid()),
            name: String(g.name ?? ""),
            taxId: String(g.taxId ?? ""),
            employees: g.employees === null || g.employees === undefined ? null : Number(g.employees) || 0,
            covered: g.covered === true ? true : g.covered === false ? false : null,
          }));
      }
    }
  } catch (err) { trackWarning("Failed to parse affiliationData", { error: String(err) }); }

  // Drop any ghost ownership assignments — rows whose entityId isn't the primary
  // sentinel and doesn't match a currently-known affiliated group id. These can
  // exist on forms saved before the ADO-3490 cascade-on-path-change fix landed:
  // the assignment referenced an affiliated entity that got removed when the user
  // flipped off the multi-employer path, and the JSON blob persisted the orphan.
  // Backend BuildOwnership already silently skips these on submit; matching the
  // filter at load keeps the store clean and lets the unassigned-owner warning
  // fire correctly for their owners.
  {
    const knownEntityIds = new Set<string>([PRIMARY_ENTITY_ID, ...affiliatedGroups.map((g) => g.id)]);
    const cleaned = ownershipAssignments.filter((a) => knownEntityIds.has(a.entityId));
    if (cleaned.length !== ownershipAssignments.length) {
      trackWarning("Dropped ghost ownership assignments on load", {
        dropped: String(ownershipAssignments.length - cleaned.length),
      });
      ownershipAssignments = cleaned;
    }
  }

  const data: IntakeFormData = {
    ...EMPTY_FORM_DATA,
    companyLegalName: raw.companyLegalName ?? "",
    effectiveDate: raw.effectiveDate ?? "",
    sicCode: (raw.sicCode ?? "").replace(/^0+/, "") || (raw.sicCode ? "0" : ""),
    sicDescription: raw.sicDescription ?? "",
    dba: raw.dba ?? "",
    federalTaxId: raw.federalTaxId ?? "",
    streetAddress: raw.streetAddress ?? "",
    city: raw.city ?? "",
    state: raw.state ?? "",
    zipCode: raw.zipCode ?? "",
    orgType: raw.organizationType ?? "",
    yearEstablished: raw.yearEstablished !== null && raw.yearEstablished !== undefined ? String(raw.yearEstablished) : "",
    avgW2Employees: raw.avgW2Employees ?? null,
    avgW2Employees20Weeks: raw.avgW2Employees20Weeks ?? null,
    contactName: raw.contactName ?? "",
    contactTitle: raw.contactTitle ?? "",
    contactEmail: raw.contactEmail ?? "",
    contactPhone: raw.contactPhone ?? "",
    fiduciarySameAsContact: (raw.fiduciarySameAsContact ?? null) as "Yes" | "No" | null,
    fiduciaryName: raw.fiduciaryName ?? "",
    fiduciaryTitle: raw.fiduciaryTitle ?? "",
    fiduciaryEmail: raw.fiduciaryEmail ?? "",
    fiduciaryPhone: raw.fiduciaryPhone ?? "",
    needAlternatePortalAccess: (raw.needAlternatePortalAccess ?? null) as "Yes" | "No" | null,
    portalContactName: raw.portalContactName ?? "",
    portalContactEmail: raw.portalContactEmail ?? "",
    portalContactPhone: raw.portalContactPhone ?? "",
    portalReplaces: raw.portalReplaces ?? "",
    // Step 3: Company Structure
    hasCba: (raw.hasCba === "Yes" || raw.hasCba === "No") ? raw.hasCba : null,
    cbaUnitName: raw.cbaUnitName ?? "",
    cbaEffectiveDate: raw.cbaEffectiveDate ?? "",
    hasPeo: (raw.hasPeo === "Yes" || raw.hasPeo === "No") ? raw.hasPeo : null,
    peoType: raw.peoType ?? "",
    isMultiEmployer: (raw.isMultipleEmployer === "Yes" || raw.isMultipleEmployer === "No") ? raw.isMultipleEmployer as "Yes" | "No" : null,
    multiEmployerType: raw.multipleEmployerType ?? "",
    isCommonlyControlledGroup: (raw.isCommonlyControlledGroup === "Yes" || raw.isCommonlyControlledGroup === "No") ? raw.isCommonlyControlledGroup : null,
    owners,
    affiliatedGroups,
    primaryEligibleEmployees,
    ownershipAssignments,
    // Step 4: Coverage & Benefits
    replacingMedical: (raw.replacingMedical === "Yes" || raw.replacingMedical === "No") ? raw.replacingMedical : null,
    priorMedicalCarrier: raw.priorMedicalCarrier ?? "",
    priorMedicalCarrierOther: raw.priorMedicalCarrierOther ?? "",
    priorMedicalPlanType: raw.priorMedicalPlanType ?? "",
    priorMedicalEffectiveDate: raw.priorMedicalEffectiveDate ?? "",
    replacingDental: (raw.replacingDental === "Yes" || raw.replacingDental === "No") ? raw.replacingDental : null,
    deductibleYearType: raw.deductibleYearType ?? "",
    medicalContribution: raw.medicalContribution ?? 50,
    offeringDental: (raw.offeringDental === "Yes" || raw.offeringDental === "No") ? raw.offeringDental : null,
    offeringVision: (raw.offeringVision === "Yes" || raw.offeringVision === "No") ? raw.offeringVision : null,
    reimburseCopays: (raw.reimburseCopays === "Yes" || raw.reimburseCopays === "No") ? raw.reimburseCopays : null,
    offerHsa: (raw.offerHsa === "Yes" || raw.offerHsa === "No") ? raw.offerHsa : null,
    offerHra: (raw.offerHra === "Yes" || raw.offerHra === "No") ? raw.offerHra : null,
    otherReimbursement: (raw.otherReimbursement === "Yes" || raw.otherReimbursement === "No") ? raw.otherReimbursement : null,
    // Step 5: Eligibility
    excludeClass: (raw.excludeClass === "Yes" || raw.excludeClass === "No") ? raw.excludeClass : null,
    excludedClasses: tryParseJsonArray(raw.excludedClassesData, "excludedClassesData"),
    excludedClassesOther: raw.excludedClassesOther ?? "",
    allowDomesticPartners: (raw.allowDomesticPartners === "Yes" || raw.allowDomesticPartners === "No") ? raw.allowDomesticPartners : null,
    minHoursPerWeek: raw.minHoursPerWeek ?? 30,
    waitingPeriod: raw.waitingPeriod ?? "",
    waiveWaitingPeriod: (raw.waiveWaitingPeriod === "Yes" || raw.waiveWaitingPeriod === "No") ? raw.waiveWaitingPeriod : null,
    employeesInWaitingPeriod: (raw.employeesInWaitingPeriod === "Yes" || raw.employeesInWaitingPeriod === "No") ? raw.employeesInWaitingPeriod : null,
    offerCOBRAVoluntary: (raw.offerCOBRAVoluntary === "Yes" || raw.offerCOBRAVoluntary === "No") ? raw.offerCOBRAVoluntary : null,
    employeesOnCOBRA: (raw.employeesOnCOBRA === "Yes" || raw.employeesOnCOBRA === "No") ? raw.employeesOnCOBRA : null,
    useOptimylCOBRA: (raw.useOptimylCOBRA === "Yes" || raw.useOptimylCOBRA === "No") ? raw.useOptimylCOBRA : null,
    wantOptimylSection125: (raw.wantOptimylSection125 === "Yes" || raw.wantOptimylSection125 === "No") ? raw.wantOptimylSection125 : null,
    section125PlanMedical: raw.sec125PlanMedical ?? false,
    section125PlanDental: raw.sec125PlanDental ?? false,
    section125PlanVision: raw.sec125PlanVision ?? false,
    section125TierEmployeeOnly: raw.sec125TierEmployeeOnly ?? false,
    section125TierEmployeeSpouse: raw.sec125TierEmployeeSpouse ?? false,
    section125TierEmployeeChildren: raw.sec125TierEmployeeChildren ?? false,
    section125TierEmployeeFamily: raw.sec125TierEmployeeFamily ?? false,
    // Step 6: Payment — fall back to EMPTY_FORM_DATA defaults (ACH / Checking)
    // when the backend hasn't seen these values yet.
    paymentMethod: raw.paymentMethod ?? EMPTY_FORM_DATA.paymentMethod,
    achAccountHolderName: raw.achAccountHolderName ?? "",
    achBankName: raw.achBankName ?? "",
    achRoutingNumber: raw.achRoutingNumber ?? "",
    achAccountNumber: raw.achAccountNumber ?? "",
    achAccountType: raw.achAccountType ?? EMPTY_FORM_DATA.achAccountType,
    // Step 7: Documents & Employees
    totalEnrolledEmployees: raw.totalEnrolledEmployees ?? null,
    docVerificationType: raw.docVerificationType ?? "",
    newlyFormedCompany: (raw.newlyFormedCompany === "Yes" || raw.newlyFormedCompany === "No") ? raw.newlyFormedCompany : null,
    documents: { ...EMPTY_FORM_DATA.documents },
    ownerEligibilityDocs: [],
    employees: mapCensusMembersToEmployees(raw.censusMembers),
  };

  // Restore uploaded files from backend into documents / ownerEligibilityDocs.
  // The backend returns ContentVersion records linked to the intake form record
  // with Description = "Employer Intake: {docType}", and the service parses
  // docType back out — so each file maps cleanly to a documents.* slot by docType.
  if (raw.files && raw.files.length > 0) {
    const knownDocTypes = new Set<keyof IntakeFormData["documents"]>([
      "wageTax", "payroll", "newBusinessDocs", "priorBill", "voidedCheck", "censusFile", "binderCheck",
    ]);
    for (const f of raw.files) {
      const ref = { fileId: f.fileId, name: f.name, size: f.size };
      if (knownDocTypes.has(f.docType as keyof IntakeFormData["documents"])) {
        data.documents[f.docType as keyof IntakeFormData["documents"]] = ref;
      } else if (f.docType === "ownerEligibilityDocs") {
        data.ownerEligibilityDocs.push(ref);
      } else {
        trackWarning("Unknown docType in file restoration", { docType: f.docType, fileId: f.fileId });
      }
    }
  }

  // A census file's origin is told by the presence of parsed members: the
  // Claims flow attaches a file with NO members; the non-Claims flow always
  // parses uploads into members. After a UW-method flip the carried-over file
  // is the wrong template, so reconcile both directions on load:
  //  - non-Claims → Claims (members present): drop the stale file + members.
  //  - Claims → non-Claims (file but no members): drop the stale file.
  const staleFileIds: string[] = [];
  let employeesModified = false;
  if (raw.uwMethod === "Claims" && (raw.censusMembers?.length ?? 0) > 0) {
    const staleFileId = data.documents.censusFile?.fileId;
    if (staleFileId) {
      staleFileIds.push(staleFileId);
      data.documents.censusFile = null;
    }
    data.employees = [];
    employeesModified = true;
  } else if (raw.uwMethod && raw.uwMethod !== "Claims"
    && (raw.censusMembers?.length ?? 0) === 0
    && data.documents.censusFile?.fileId) {
    // Quote flipped Claims → non-Claims: the attached census came from the
    // Claims validate-and-attach flow (no parsed members, wrong template), so
    // drop it and let the non-Claims flow start clean. Guard on a known UW
    // value — a null uwMethod means "not observed", not "non-Claims", and must
    // not delete a valid Claims census.
    staleFileIds.push(data.documents.censusFile.fileId);
    data.documents.censusFile = null;
  }

  // Clear stale employee selections when plan metadata changes:
  // - Medical plan out of range (e.g. had Plan 4, now only 2 plans)
  // - Dental/vision selections when Plan 1 no longer offers them
  //
  // Each metadata signal is null when the backend didn't observe it (no Opp / no quote /
  // query failed). In that case we deliberately skip the corresponding clean-up — otherwise
  // a transient "metadata not loaded" state would silently wipe user selections.
  if (data.employees.length > 0) {
    for (const emp of data.employees) {
      // Guard `> 0` — availablePlans = 0 from a backend regression would otherwise
      // produce an empty validPlans set and silently wipe every medical plan selection.
      if (raw.availablePlans !== null && raw.availablePlans !== undefined && raw.availablePlans > 0) {
        const validPlans = new Set(
          Array.from({ length: raw.availablePlans }, (_, i) => `Plan ${i + 1}`),
        );
        if (emp.medicalPlan && !validPlans.has(emp.medicalPlan)) {
          emp.medicalPlan = "";
          employeesModified = true;
        }
      }
      // Dental/Vision offered is derived from the user's own Coverage-page answer
      // (data.offeringDental / data.offeringVision) — the single source of truth.
      // null means "not yet answered", in which case skip the trim to avoid wiping
      // selections on a partially-filled form.
      if (data.offeringDental === "No" && emp.dentalCoverage && emp.dentalCoverage !== "N/A") {
        emp.dentalCoverage = "N/A";
        employeesModified = true;
      } else if (data.offeringDental === "Yes" && emp.dentalCoverage === "N/A") {
        emp.dentalCoverage = "";
        employeesModified = true;
      }
      if (data.offeringVision === "No" && emp.visionCoverage && emp.visionCoverage !== "N/A") {
        emp.visionCoverage = "N/A";
        employeesModified = true;
      } else if (data.offeringVision === "Yes" && emp.visionCoverage === "N/A") {
        emp.visionCoverage = "";
        employeesModified = true;
      }
    }
  }

  return {
    id: raw.id,
    proposalId: raw.opportunityId,
    version: raw.version,
    status: raw.status === "Submitted" ? "submitted" : "draft",
    createdAt: raw.createdAt,
    updatedAt: raw.updatedAt,
    data,
    prefilled,
    baseline,
    mismatches,
    meta: {
      organizationTypeOptions: raw.meta?.organizationTypeOptions ?? [],
      medicalCarrierOptions: raw.meta?.medicalCarrierOptions ?? [],
      excludedClassesOptions: raw.meta?.excludedClassesOptions ?? [],
      waitingPeriodOptions: raw.meta?.waitingPeriodOptions ?? [],
      sicCodes: [],
      uwMethod: raw.uwMethod ?? null,
      isRenewal: raw.isRenewal === true,
      availablePlans: raw.availablePlans ?? 1,
      ihqLink: raw.ihqLink ?? null,
    },
    staleFileIds,
    employeesModified,
  };
}

let sicCodesCache: SicCodeEntry[] | null = null;

async function fetchSicCodes(): Promise<SicCodeEntry[]> {
  if (sicCodesCache) return sicCodesCache;
  try {
    const result = await apiFetch<SicCodeEntry[]>("/sic-codes");
    if (result.length > 0) sicCodesCache = result;
    return result;
  } catch {
    return [];
  }
}

async function enrichWithSicCodes(response: IntakeFormResponse): Promise<IntakeFormResponse> {
  const sicCodes = await fetchSicCodes();
  response.meta.sicCodes = sicCodes;
  return response;
}

export async function getIntakeForm(formId: string): Promise<IntakeFormResponse> {
  const raw = await apiFetch<BackendIntakeResponse>(`/employer-intake/${formId}`);
  return enrichWithSicCodes(mapToIntakeFormResponse(raw));
}

export async function getIntakeFormByProposal(
  proposalId: string,
): Promise<IntakeFormResponse | null> {
  // 204 is the "Opp exists, no form yet" signal — caller creates V1.
  const raw = await apiFetch<BackendIntakeResponse | undefined>(
    `/employer-intake/opportunity/${proposalId}/latest`,
  );
  if (!raw) return null;
  return enrichWithSicCodes(mapToIntakeFormResponse(raw));
}

export async function createIntakeForm(
  proposalId: string,
): Promise<IntakeFormResponse> {
  const raw = await apiFetch<BackendIntakeResponse>("/employer-intake", {
    method: "POST",
    body: JSON.stringify({ opportunityId: proposalId }),
  });
  return enrichWithSicCodes(mapToIntakeFormResponse(raw));
}

/**
 * Maps the store's camelCase field names to the backend UpdateEmployerIntakeRequest
 * shape. Most fields pass through verbatim; the handful that use different names
 * at the SF/DTO boundary (orgType → organizationType) are renamed here, and
 * yearEstablished is coerced from its masked string form to an int.
 *
 * The `!== undefined` guard on every field is the consuming half of a three-state
 * contract with the backend (AB#3593): an omitted field means "leave Salesforce
 * alone", `""` means "the user cleared this, blank it", and a value is written.
 * So a cleared field must be forwarded as `""`, never dropped. What gets omitted
 * is decided upstream by stripInvalidFieldsFromSnapshot in validation/validate.ts.
 */
function toUpdateRequest(data: Partial<IntakeFormData>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  if (data.companyLegalName !== undefined) out.companyLegalName = data.companyLegalName;
  if (data.effectiveDate !== undefined) out.effectiveDate = data.effectiveDate;
  if (data.sicCode !== undefined) out.sicCode = data.sicCode;
  if (data.sicDescription !== undefined) out.sicDescription = data.sicDescription;
  if (data.dba !== undefined) out.dba = data.dba;
  if (data.federalTaxId !== undefined) out.federalTaxId = data.federalTaxId;
  if (data.streetAddress !== undefined) out.streetAddress = data.streetAddress;
  if (data.city !== undefined) out.city = data.city;
  if (data.state !== undefined) out.state = data.state;
  if (data.zipCode !== undefined) out.zipCode = data.zipCode;
  if (data.orgType !== undefined) out.organizationType = data.orgType;
  if (data.yearEstablished !== undefined) {
    const n = parseInt(data.yearEstablished, 10);
    out.yearEstablished = Number.isFinite(n) ? n : null;
  }
  if (data.avgW2Employees !== undefined) out.avgW2Employees = data.avgW2Employees;
  if (data.avgW2Employees20Weeks !== undefined) out.avgW2Employees20Weeks = data.avgW2Employees20Weeks;
  if (data.contactName !== undefined) out.contactName = data.contactName;
  if (data.contactTitle !== undefined) out.contactTitle = data.contactTitle;
  if (data.contactEmail !== undefined) out.contactEmail = data.contactEmail;
  if (data.contactPhone !== undefined) out.contactPhone = data.contactPhone;
  if (data.fiduciarySameAsContact !== undefined) out.fiduciarySameAsContact = data.fiduciarySameAsContact;
  if (data.fiduciaryName !== undefined) out.fiduciaryName = data.fiduciaryName;
  if (data.fiduciaryTitle !== undefined) out.fiduciaryTitle = data.fiduciaryTitle;
  if (data.fiduciaryEmail !== undefined) out.fiduciaryEmail = data.fiduciaryEmail;
  if (data.fiduciaryPhone !== undefined) out.fiduciaryPhone = data.fiduciaryPhone;
  if (data.needAlternatePortalAccess !== undefined) out.needAlternatePortalAccess = data.needAlternatePortalAccess;
  if (data.portalContactName !== undefined) out.portalContactName = data.portalContactName;
  if (data.portalContactEmail !== undefined) out.portalContactEmail = data.portalContactEmail;
  if (data.portalContactPhone !== undefined) out.portalContactPhone = data.portalContactPhone;
  if (data.portalReplaces !== undefined) out.portalReplaces = data.portalReplaces;
  // Step 3 — picklist toggles (pass "Yes" / "No" / null directly)
  if (data.hasCba !== undefined) out.hasCba = data.hasCba;
  if (data.cbaUnitName !== undefined) out.cbaUnitName = data.cbaUnitName;
  if (data.cbaEffectiveDate !== undefined) out.cbaEffectiveDate = data.cbaEffectiveDate;
  if (data.hasPeo !== undefined) out.hasPeo = data.hasPeo;
  if (data.peoType !== undefined) out.peoType = data.peoType;
  // Step 3 — renamed fields
  if (data.isMultiEmployer !== undefined) out.isMultipleEmployer = data.isMultiEmployer;
  if (data.multiEmployerType !== undefined) out.multipleEmployerType = data.multiEmployerType;
  if (data.isCommonlyControlledGroup !== undefined) out.isCommonlyControlledGroup = data.isCommonlyControlledGroup;
  // Step 3 — JSON-serialized. ownershipData bundles the shared owner roster + the
  // per-(owner, entity) assignment matrix in one blob so the backend gets both
  // halves atomically. Any change to either half re-serializes the whole thing.
  if (data.owners !== undefined || data.ownershipAssignments !== undefined) {
    out.ownershipData = JSON.stringify({
      owners: data.owners,
      assignments: data.ownershipAssignments,
    });
  }
  if (data.affiliatedGroups !== undefined) {
    // Prepend a same-shaped entry for the primary group when the affiliated-entity
    // list is active on the intake — that way Affiliation_Data__c contains one row
    // per Affiliation_Group__c we want on submit (including primary), and downstream
    // consumers (BuildAffiliationGroups, the SF record-page controller) don't need
    // a separate primary path. Callers (saveDraft's full-snapshot save) pass the
    // path fields alongside affiliatedGroups so this composition sees them; the
    // employees mini-save doesn't touch affiliatedGroups so this branch skips.
    const pathActive = isAffiliatedListShown({
      isMultiEmployer: data.isMultiEmployer ?? null,
      multiEmployerType: data.multiEmployerType ?? "",
      isCommonlyControlledGroup: data.isCommonlyControlledGroup ?? null,
    });
    const list = pathActive
      ? [{
          id: PRIMARY_ENTITY_ID,
          name: data.companyLegalName ?? "",
          taxId: data.federalTaxId ?? "",
          employees: data.primaryEligibleEmployees ?? null,
          covered: true,
        }, ...data.affiliatedGroups]
      : data.affiliatedGroups;
    out.affiliationData = JSON.stringify(list);
  }
  // Step 4: Coverage & Benefits
  if (data.replacingMedical !== undefined) out.replacingMedical = data.replacingMedical;
  if (data.priorMedicalCarrier !== undefined) out.priorMedicalCarrier = data.priorMedicalCarrier;
  if (data.priorMedicalCarrierOther !== undefined) out.priorMedicalCarrierOther = data.priorMedicalCarrierOther;
  if (data.priorMedicalPlanType !== undefined) out.priorMedicalPlanType = data.priorMedicalPlanType;
  if (data.priorMedicalEffectiveDate !== undefined) out.priorMedicalEffectiveDate = data.priorMedicalEffectiveDate;
  if (data.replacingDental !== undefined) out.replacingDental = data.replacingDental;
  if (data.deductibleYearType !== undefined) out.deductibleYearType = data.deductibleYearType;
  if (data.medicalContribution !== undefined) out.medicalContribution = data.medicalContribution;
  if (data.offeringDental !== undefined) out.offeringDental = data.offeringDental;
  if (data.offeringVision !== undefined) out.offeringVision = data.offeringVision;
  if (data.reimburseCopays !== undefined) out.reimburseCopays = data.reimburseCopays;
  if (data.offerHsa !== undefined) out.offerHsa = data.offerHsa;
  if (data.offerHra !== undefined) out.offerHra = data.offerHra;
  if (data.otherReimbursement !== undefined) out.otherReimbursement = data.otherReimbursement;
  // Step 5: Eligibility
  if (data.excludeClass !== undefined) out.excludeClass = data.excludeClass;
  if (data.excludedClasses !== undefined) out.excludedClassesData = JSON.stringify(data.excludedClasses);
  if (data.excludedClassesOther !== undefined) out.excludedClassesOther = data.excludedClassesOther;
  if (data.allowDomesticPartners !== undefined) out.allowDomesticPartners = data.allowDomesticPartners;
  if (data.minHoursPerWeek !== undefined) out.minHoursPerWeek = data.minHoursPerWeek;
  if (data.waitingPeriod !== undefined) out.waitingPeriod = data.waitingPeriod;
  if (data.waiveWaitingPeriod !== undefined) out.waiveWaitingPeriod = data.waiveWaitingPeriod;
  if (data.employeesInWaitingPeriod !== undefined) out.employeesInWaitingPeriod = data.employeesInWaitingPeriod;
  if (data.offerCOBRAVoluntary !== undefined) out.offerCOBRAVoluntary = data.offerCOBRAVoluntary;
  if (data.employeesOnCOBRA !== undefined) out.employeesOnCOBRA = data.employeesOnCOBRA;
  if (data.useOptimylCOBRA !== undefined) out.useOptimylCOBRA = data.useOptimylCOBRA;
  if (data.wantOptimylSection125 !== undefined) out.wantOptimylSection125 = data.wantOptimylSection125;
  if (data.section125PlanMedical !== undefined) out.sec125PlanMedical = data.section125PlanMedical;
  if (data.section125PlanDental !== undefined) out.sec125PlanDental = data.section125PlanDental;
  if (data.section125PlanVision !== undefined) out.sec125PlanVision = data.section125PlanVision;
  if (data.section125TierEmployeeOnly !== undefined) out.sec125TierEmployeeOnly = data.section125TierEmployeeOnly;
  if (data.section125TierEmployeeSpouse !== undefined) out.sec125TierEmployeeSpouse = data.section125TierEmployeeSpouse;
  if (data.section125TierEmployeeChildren !== undefined) out.sec125TierEmployeeChildren = data.section125TierEmployeeChildren;
  if (data.section125TierEmployeeFamily !== undefined) out.sec125TierEmployeeFamily = data.section125TierEmployeeFamily;
  // Step 6: Payment
  if (data.paymentMethod !== undefined) out.paymentMethod = data.paymentMethod;
  if (data.achAccountHolderName !== undefined) out.achAccountHolderName = data.achAccountHolderName;
  if (data.achBankName !== undefined) out.achBankName = data.achBankName;
  if (data.achRoutingNumber !== undefined) out.achRoutingNumber = data.achRoutingNumber;
  if (data.achAccountNumber !== undefined) out.achAccountNumber = data.achAccountNumber;
  if (data.achAccountType !== undefined) out.achAccountType = data.achAccountType;
  // Step 7 — picklist fields + census members
  if (data.docVerificationType !== undefined) out.docVerificationType = data.docVerificationType;
  if (data.employees !== undefined) {
    out.employees = data.employees.map((emp) => ({
      // Forward salesforceId as `id` so the backend can diff: known ids become UPDATEs,
      // missing ids become INSERTs, and disappearing ids become DELETEs.
      id: emp.salesforceId ?? null,
      firstName: emp.firstName,
      lastName: emp.lastName,
      owner: emp.owner,
      medicalCoverage: emp.medicalCoverage,
      medicalPlan: emp.medicalPlan || null,
      dentalCoverage: emp.dentalCoverage || null,
      visionCoverage: emp.visionCoverage || null,
      cobra: emp.cobra,
    }));
  }
  if (data.newlyFormedCompany !== undefined) out.newlyFormedCompany = data.newlyFormedCompany;
  return out;
}

export async function saveDraft(
  formId: string,
  data: Partial<IntakeFormData>,
): Promise<IntakeFormResponse> {
  const payload: Record<string, unknown> = toUpdateRequest(data);

  // Backend (ADO-3405 phase 7) requires X-Form-Session-Id to verify the caller
  // still owns the form lock before persisting.
  const sessionId = useLockStore.getState().sessionId;

  const raw = await apiFetch<BackendIntakeResponse>(`/employer-intake/${formId}/draft`, {
    method: "PUT",
    headers: { "X-Form-Session-Id": sessionId },
    body: JSON.stringify(payload),
    // 409 means the lock was lost (another user took over). The save layer
    // handles this specifically by flipping the UI to RemovedScreen — suppress
    // the generic "API error: 409" toast that apiFetch would otherwise emit.
    allowedStatuses: [409],
  });

  return mapToIntakeFormResponse(raw);
}

export async function submitIntakeForm(
  formId: string,
): Promise<IntakeFormResponse> {
  // Submit is the slowest endpoint by design (cross-object SF composite + child reconcile);
  // typical end-to-end is 4-6s, worst observed ~16s under sandbox load. Use a 60s ceiling
  // so true SF-side hangs still surface but normal-slow submissions don't false-positive.
  const raw = await apiFetch<BackendIntakeResponse>(`/employer-intake/${formId}/submit`, {
    method: "POST",
    timeoutMs: 60_000,
  });
  return mapToIntakeFormResponse(raw);
}

export async function uploadFile(
  formId: string,
  docType: string,
  file: File,
): Promise<FileUploadResponse> {
  // Step 1: Get a SAS upload URL from the backend (tiny JSON request)
  const { uploadUrl, blobName } = await apiFetch<{ uploadUrl: string; blobName: string }>(
    `/employer-intake/${formId}/files/upload-url`,
    {
      method: "POST",
      body: JSON.stringify({ fileName: file.name, docType, fileSize: file.size }),
    },
  );

  // Step 2: Upload file directly to Blob Storage (bypasses Azure Function size limits)
  const uploadRes = await fetch(uploadUrl, {
    method: "PUT",
    headers: {
      "x-ms-blob-type": "BlockBlob",
      "Content-Type": file.type || "application/octet-stream",
    },
    body: file,
  });
  if (!uploadRes.ok) {
    throw new Error(`Blob upload failed: ${uploadRes.status}`);
  }

  // Step 3: Tell the backend to move the file from blob to Salesforce (tiny JSON request)
  return apiFetch<FileUploadResponse>(`/employer-intake/${formId}/files/complete`, {
    method: "POST",
    body: JSON.stringify({ blobName, fileName: file.name, docType }),
    timeoutMs: 300_000, // 5 min — backend reads blob + uploads to Salesforce
  });
}

export async function deleteFile(fileId: string): Promise<void> {
  const formId = useFormStore.getState().formId;
  if (!formId) {
    trackWarning("intakeApi.deleteFile: no formId in store, skipping", { fileId });
    return;
  }
  await apiFetch<void>(`/employer-intake/${formId}/files/${fileId}`, {
    method: "DELETE",
  });
}

export async function acquireLock(
  formId: string,
  email: string,
): Promise<LockResponse> {
  return apiFetch<LockResponse>(`/employer-intake/${formId}/lock`, {
    method: "POST",
    body: JSON.stringify({ email }),
  });
}

export async function releaseLock(formId: string): Promise<void> {
  await apiFetch<void>(`/employer-intake/${formId}/lock`, {
    method: "DELETE",
  });
}
